import { inject, Injectable } from '@angular/core';
import { ITransactionsState, ITransactionsQueryParams, ITransaction } from 'src/models/transactions.model';
import { signalStore, withState, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, distinctUntilChanged, map, of, pipe, switchMap, tap } from 'rxjs';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { withDevtools, updateState } from '@angular-architects/ngrx-toolkit';
import { RestApiService } from 'src/services/rest-api.service';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';
import isEqual from 'lodash/isEqual';

enum ETransactionsActions {
  TRANSACTIONS_REQUEST = '[Transactions] Transactions Request',
  TRANSACTIONS_SUCCESS = '[Transactions] Transactions Success',
  TRANSACTIONS_FAILURE = '[Transactions] Transactions Failure',
  TRANSACTIONS_SEARCH_REQUEST = '[Transactions] Transactions Search Request',
  TRANSACTIONS_SEARCH_SUCCESS = '[Transactions] Transactions Search Success',
  TRANSACTIONS_SEARCH_FAILURE = '[Transactions] Transactions Search Failure',
  TRANSACTION_DETAIL_SELECTED = '[Transactions] Transaction Detail Selected',
  CLEAR_TRANSACTION_DETAIL_SELECTED = '[Transactions] Clear Transaction Detail Selected',
}

const initialTransactionsState: ITransactionsState = {
  transactions: [],
  transactionsForSearchCount: null,
  totalCount: null,
  selectedTransaction: null,
};

const STORE_NAME = 'transactions';

@Injectable({ providedIn: 'root' })
export class TransactionsStore extends signalStore(
  withState(initialTransactionsState),
  withDevtools(STORE_NAME),
  withRequestStatus(),
  withMethods((store, restApiService = inject(RestApiService)) => {

    const getTransactions = rxMethod<ITransactionsQueryParams>(
      pipe(
        distinctUntilChanged((a, b) => isEqual(a, b)),
        tap(() =>
          updateState(store, ETransactionsActions.TRANSACTIONS_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.TRANSACTIONS, loadingProps),
          }))
        ),
        switchMap((queryParams) =>
          restApiService.getTransactions(queryParams).pipe(
            map((response) =>
              updateState(store, ETransactionsActions.TRANSACTIONS_SUCCESS, ({ loadingProps, errorProps }) => ({
                transactions: response.content,
                totalCount: response.totalCount,
                ...withoutLoading(ELoadingEntity.TRANSACTIONS, loadingProps),
                ...withoutError(ELoadingEntity.TRANSACTIONS, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, ETransactionsActions.TRANSACTIONS_FAILURE, ({ loadingProps, errorProps }) => ({
                  transactions: [],
                  totalCount: null,
                  ...withoutLoading(ELoadingEntity.TRANSACTIONS, loadingProps),
                  ...withError(ELoadingEntity.TRANSACTIONS, { error, errorParams: queryParams }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getTransactionsForSearch = rxMethod<ITransactionsQueryParams>(
      pipe(
        tap(() =>
          updateState(store, ETransactionsActions.TRANSACTIONS_SEARCH_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.TRANSACTIONS_SEARCH, loadingProps),
          }))
        ),
        switchMap((queryParams) =>
          restApiService.getTransactions(queryParams).pipe(
            map((response) =>
              updateState(store, ETransactionsActions.TRANSACTIONS_SEARCH_SUCCESS, ({ loadingProps, errorProps }) => ({
                transactionsForSearchCount: response.totalCount,
                ...withoutLoading(ELoadingEntity.TRANSACTIONS_SEARCH, loadingProps),
                ...withoutError(ELoadingEntity.TRANSACTIONS_SEARCH, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, ETransactionsActions.TRANSACTIONS_SEARCH_FAILURE, ({ loadingProps, errorProps }) => ({
                  transactionsForSearchCount: null,
                  ...withoutLoading(ELoadingEntity.TRANSACTIONS_SEARCH, loadingProps),
                  ...withError(
                    ELoadingEntity.TRANSACTIONS_SEARCH,
                    { error, errorParams: queryParams },
                    errorProps,
                  ),
                }))
              )
            )
          )
        )
      )
    );

    const clearSelectedTransaction = () => {
      updateState(store, ETransactionsActions.CLEAR_TRANSACTION_DETAIL_SELECTED, { selectedTransaction: null });
    };

    const setSelectedTransaction = (transaction: ITransaction) => {
      updateState(store, ETransactionsActions.TRANSACTION_DETAIL_SELECTED, { selectedTransaction: transaction });
    };

    return {
      getTransactions,
      getTransactionsForSearch,
      clearSelectedTransaction,
      setSelectedTransaction,
    };
  })
) {}

